import ApiService from '../services/api/api_service';
import { getToken } from 'firebase/messaging';
import { messaging } from '../config/firebase-config';

// Global cache to prevent duplicate API calls
let sessionCache = new Map();
let fcmTokenCache = null;
let fcmTokenCacheTime = null;

const SESSION_CACHE_DURATION = 10000; // 10 seconds
const FCM_TOKEN_CACHE_DURATION = 300000; // 5 minutes

/**
 * Session management utility for tracking user sessions and device information
 * Similar to mobile app's sendSessionData function
 */
class SessionService {
  static deviceUuid = null;

  /**
   * Generate or retrieve device UUID from localStorage
   */
  static getDeviceUuid() {
    if (!this.deviceUuid) {
      this.deviceUuid = localStorage.getItem('device_uuid');
      if (!this.deviceUuid) {
        this.deviceUuid = this.generateUuid();
        localStorage.setItem('device_uuid', this.deviceUuid);
      }
    }
    return this.deviceUuid;
  }

  /**
   * Generate UUID v4
   */
  static generateUuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Get device information
   */
  static getDeviceInfo() {
    const userAgent = navigator.userAgent;
    let deviceName = 'Unknown Device';
    let deviceOS = 'Unknown OS';

    // Detect OS
    if (userAgent.includes('Windows')) {
      deviceOS = 'Windows';
      deviceName = 'Windows PC';
    } else if (userAgent.includes('Mac')) {
      deviceOS = 'macOS';
      deviceName = 'Mac';
    } else if (userAgent.includes('Linux')) {
      deviceOS = 'Linux';
      deviceName = 'Linux PC';
    } else if (userAgent.includes('iPhone')) {
      deviceOS = 'iOS';
      deviceName = 'iPhone';
    } else if (userAgent.includes('iPad')) {
      deviceOS = 'iOS';
      deviceName = 'iPad';
    } else if (userAgent.includes('Android')) {
      deviceOS = 'Android';
      deviceName = 'Android Device';
    }

    return {
      deviceName,
      deviceOS,
      userAgent
    };
  }

  /**
   * Get public IP address
   */
  static async getPublicIP() {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip || '';
    } catch (error) {
      console.warn('Could not fetch public IP:', error);
      return '';
    }
  }

  /**
   * Get FCM token for web push notifications
   */
  static async getFCMToken() {
    if (!messaging) {
      console.warn('Firebase Messaging not available, skipping FCM token retrieval');
      return null;
    }
    
    const now = Date.now();
    
    // Return cached FCM token if still valid
    if (fcmTokenCache && 
        fcmTokenCacheTime && 
        (now - fcmTokenCacheTime) < FCM_TOKEN_CACHE_DURATION) {
      console.log('SessionService: Using cached FCM token');
      return fcmTokenCache;
    }

    try {
      // Check if notifications are supported
      if (!('Notification' in window)) {
        console.log('This browser does not support notifications');
        return null;
      }
      
      // Request permission if not granted
      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          console.log('Notification permission not granted');
          return null;
        }
      }

      if (Notification.permission !== 'granted') {
        console.log('Notification permission not granted');
        return null;
      }

      // Get FCM token
      const token = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY // You'll need to add this to env
      });

      // Cache the token
      fcmTokenCache = token;
      fcmTokenCacheTime = now;
      console.log('SessionService: FCM token retrieved and cached');

      return token;
    } catch (error) {
      console.warn('Could not get FCM token:', error);
      return null;
    }
  }

  /**
   * Check notification permission status
   */
  static getNotificationPermissionStatus() {
    if (!('Notification' in window)) {
      return false;
    }
    return Notification.permission === 'granted';
  }

  /**
   * Send session data to server
   * @param {Object} user - Firebase user object
   * @param {boolean} isLogin - Whether this is a login (true) or logout (false) event
   */
  static async sendSessionData(user, isLogin = true) {
    if (!user) {
      console.warn('No user provided to sendSessionData');
      return;
    }

    // Don't send session data for anonymous users
    if (user.isAnonymous) {
      console.log('Skipping session data for anonymous user');
      return;
    }

    const now = Date.now();
    const cacheKey = `${user.uid}_${isLogin}`;
    
    // Check cache to prevent duplicate calls
    if (sessionCache.has(cacheKey)) {
      const cacheEntry = sessionCache.get(cacheKey);
      if ((now - cacheEntry.timestamp) < SESSION_CACHE_DURATION) {
        console.log('SessionService: Using cached session data, skipping API call');
        return cacheEntry.data;
      }
    }

    try {
      const deviceInfo = this.getDeviceInfo();
      const deviceUuid = this.getDeviceUuid();
      const ipAddress = await this.getPublicIP();
      const fcmToken = await this.getFCMToken();
      const notificationEnabled = this.getNotificationPermissionStatus();

      const sessionData = {
        user_id: user.uid,
        device_uuid: deviceUuid,
        device_name: deviceInfo.deviceName,
        device_os: deviceInfo.deviceOS,
        fcm_token: fcmToken,
        ip_address: ipAddress,
        user_agent: deviceInfo.userAgent,
        notification_enabled: notificationEnabled,
        is_login: isLogin,
      };

      console.log('Sending session data:', sessionData);

      // Send to server using existing ApiService
      const result = await ApiService.post(
        '/v1/sessions',
        {
          'Content-Type': 'application/json',
          authorization: `Bearer ${await user.getIdToken()}`
        },
        JSON.stringify(sessionData),
        { reformatData: true }
      );

      // Cache the successful result
      sessionCache.set(cacheKey, {
        timestamp: now,
        data: result
      });

      console.log('Session data sent successfully');
      return result;
    } catch (error) {
      console.error('Error sending session data:', error);
      throw error;
    }
  }

  /**
   * Send login session data
   */
  static async sendLoginSession(user) {
    return this.sendSessionData(user, true);
  }

  /**
   * Send logout session data
   */
  static async sendLogoutSession(user) {
    return this.sendSessionData(user, false);
  }

  /**
   * Update session when notification permission changes
   */
  static async updateSessionOnPermissionChange(user) {
    if (user) {
      return this.sendSessionData(user, true);
    }
  }
}

export default SessionService;
